import useParameterStore from "../../store/parameterStore";
import { getAllPlacedPCells } from "../store/placedPCellStore";

const clone = (value) => JSON.parse(JSON.stringify(value));

const compactDesignParameter = (parameter = {}) => ({
  id: parameter.id || "",
  name: String(parameter.name || "").trim(),
  type: parameter.type || "I",
  defaultValue:
    parameter.defaultValue ?? parameter.defaultVal ?? parameter.value ?? "",
  groupName: String(parameter.groupName || "").trim(),
  ranges: Array.isArray(parameter.ranges) ? clone(parameter.ranges) : [],
});

const compactPCellParameter = (parameter = {}) => ({
  name: String(parameter.originalName || parameter.name || "").trim(),
  type: String(parameter.type || "").trim(),
  currentValue: clone(parameter.value),
  semanticRole: String(parameter.semanticRole || "").trim(),
  aliases: Array.isArray(parameter.aliases) ? [...parameter.aliases] : [],
});

const parseMappingTarget = (mappingKey) => {
  const separatorIndex = mappingKey.lastIndexOf(":");
  if (separatorIndex <= 0 || separatorIndex === mappingKey.length - 1) {
    return null;
  }

  return {
    cellName: mappingKey.slice(0, separatorIndex),
    targetParameter: mappingKey.slice(separatorIndex + 1),
  };
};

export const buildParametricAiDesignContext = ({
  cellName,
  patternType,
  availableLayers = [],
}) => {
  const {
    globalParameters,
    localParameters,
    parameterMappings,
  } = useParameterStore.getState();

  const compactGlobal = globalParameters
    .map(compactDesignParameter)
    .filter((parameter) => parameter.name);
  const compactLocal = localParameters
    .map(compactDesignParameter)
    .filter((parameter) => parameter.name);
  const sourceByKey = new Map([
    ...compactGlobal.map((parameter) => [`global:${parameter.id}`, parameter]),
    ...compactLocal.map((parameter) => [`local:${parameter.id}`, parameter]),
  ]);

  const mappings = Object.entries(parameterMappings || {})
    .map(([mappingKey, mapping]) => {
      const target = parseMappingTarget(mappingKey);
      if (!target || !mapping) return null;

      const source = sourceByKey.get(
        `${mapping.sourceScope}:${mapping.sourceId}`
      );

      return {
        ...target,
        sourceScope: mapping.sourceScope,
        sourceId: mapping.sourceId,
        sourceName: source?.name || mapping.sourceName || "",
        sourceType: source?.type || "",
        sourceValue: source?.defaultValue ?? "",
      };
    })
    .filter(Boolean);

  return {
    schemaVersion: "2.0",
    selectedTarget: {
      cellName: cellName || "",
      patternType: patternType || "",
    },
    designParameters: {
      global: compactGlobal,
      local: compactLocal,
    },
    availableLayers: (Array.isArray(availableLayers) ? availableLayers : [])
      .map((layer) => ({
        layer: String(layer?.layer_name || "").trim(),
        datatype: String(layer?.datatype_name || "").trim(),
        layerNumber: layer?.layer_number ?? "",
        datatypeNumber: layer?.datatype_number ?? "",
      }))
      .filter((layer) => layer.layer && layer.datatype),
    mappings,
    placedPCells: getAllPlacedPCells().map((placed) => ({
      cellName: placed.cellName,
      patternType: placed.patternType,
      parameters: (placed.parameters || [])
        .map(compactPCellParameter)
        .filter((parameter) => parameter.name && parameter.type),
    })),
  };
};
